import React, { useState } from 'react';
import { View, Text, Modal, Pressable } from 'react-native';
import { Tag, Check } from 'lucide-react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import type {
  DataRoomDocument,
  DocumentClassificationConfidence,
} from '@/lib/state/dataroom-store';
import { confidenceLabel } from '@/lib/dataroom/classify';

const TYPE_OPTIONS: Array<{ value: DataRoomDocument['type']; label: string }> = [
  { value: 'pitch_deck', label: 'Pitch Deck' },
  { value: 'financials', label: 'Financials' },
  { value: 'legal', label: 'Legal' },
  { value: 'product', label: 'Product' },
  { value: 'team', label: 'Team' },
  { value: 'market_research', label: 'Market Research' },
  { value: 'other', label: 'Other' },
];

interface DocumentTypeChipProps {
  type: DataRoomDocument['type'];
  confidence?: DocumentClassificationConfidence;
  overridden?: boolean;
  onChange: (next: DataRoomDocument['type']) => void;
  /** Disable to render as a static chip (no override available). */
  readOnly?: boolean;
}

export function DocumentTypeChip({
  type,
  confidence,
  overridden,
  onChange,
  readOnly = false,
}: DocumentTypeChipProps) {
  const colors = useThemeColors();
  const [open, setOpen] = useState(false);

  const currentLabel = TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type;
  const subLabel = overridden ? 'Manual' : confidenceLabel(confidence);

  const accent =
    confidence === 'high'
      ? colors.success
      : confidence === 'medium'
        ? colors.warning
        : confidence === 'low'
          ? colors.error
          : colors.text.tertiary;

  return (
    <>
      <AnimatedPressable
        onPress={() => !readOnly && setOpen(true)}
        disabled={readOnly}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 6,
          paddingHorizontal: 8,
          paddingVertical: 4,
          borderRadius: 999,
          backgroundColor: colors.surface.secondary,
          borderWidth: 1,
          borderColor: colors.border.light,
        }}
      >
        <Tag size={11} color={accent} />
        <Text
          style={{
            color: colors.text.primary,
            fontSize: 11,
            fontWeight: '700',
          }}
          numberOfLines={1}
        >
          {currentLabel}
        </Text>
        <Text
          style={{
            color: colors.text.tertiary,
            fontSize: 10,
            fontWeight: '500',
          }}
          numberOfLines={1}
        >
          · {subLabel}
        </Text>
      </AnimatedPressable>

      {/* Override modal */}
      <Modal
        transparent
        visible={open}
        animationType="fade"
        onRequestClose={() => setOpen(false)}
      >
        <Pressable
          onPress={() => setOpen(false)}
          style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'center', padding: 24 }}
        >
          <Pressable
            onPress={(e) => e.stopPropagation()}
            style={{
              backgroundColor: colors.surface.primary,
              borderRadius: 16,
              padding: 8,
              maxWidth: 360,
              alignSelf: 'center',
              width: '100%',
            }}
          >
            <Text
              style={{
                color: colors.text.tertiary,
                fontSize: 11,
                fontWeight: '700',
                letterSpacing: 0.6,
                textTransform: 'uppercase',
                paddingHorizontal: 12,
                paddingVertical: 8,
              }}
            >
              Set Document Type
            </Text>
            {TYPE_OPTIONS.map((opt) => {
              const selected = opt.value === type;
              return (
                <AnimatedPressable
                  key={opt.value}
                  onPress={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingHorizontal: 12,
                    paddingVertical: 10,
                    borderRadius: 10,
                    backgroundColor: selected ? colors.primary[50] : 'transparent',
                  }}
                >
                  <Text
                    style={{
                      color: selected ? colors.primary[700] : colors.text.primary,
                      fontSize: 14,
                      fontWeight: selected ? '700' : '500',
                    }}
                  >
                    {opt.label}
                  </Text>
                  {selected && <Check size={16} color={colors.primary[600]} />}
                </AnimatedPressable>
              );
            })}
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

/**
 * Stale-comment tag — render next to a comment when it was made on an older
 * version of the document.
 */
export function StaleCommentTag({
  commentedOnVersion,
  currentVersion,
}: {
  commentedOnVersion: number;
  currentVersion: number;
}) {
  const colors = useThemeColors();
  if (commentedOnVersion >= currentVersion) return null;
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 999,
        backgroundColor: colors.amber.light,
        marginLeft: 6,
      }}
    >
      <Text
        style={{
          color: colors.warning,
          fontSize: 9,
          fontWeight: '700',
          letterSpacing: 0.3,
          textTransform: 'uppercase',
        }}
        numberOfLines={1}
      >
        On v{commentedOnVersion} · Current v{currentVersion}
      </Text>
    </View>
  );
}
